import type { Pace, Rations } from "@/game/types";
import { cn } from "@/lib/utils";

const PACES: { value: Pace; label: string; hint: string }[] = [
  { value: "steady", label: "Steady", hint: "40 mi/day · easy on party" },
  { value: "strenuous", label: "Strenuous", hint: "60 mi/day · tiring" },
  { value: "grueling", label: "Grueling", hint: "80 mi/day · brutal" },
];

const RATIONS: { value: Rations; label: string; hint: string }[] = [
  { value: "filling", label: "Filling", hint: "eats more · heals" },
  { value: "meager", label: "Meager", hint: "balanced" },
  { value: "bare-bones", label: "Bare-bones", hint: "saves food · starves" },
];

interface ControlsProps {
  pace: Pace;
  rations: Rations;
  onPace: (p: Pace) => void;
  onRations: (r: Rations) => void;
}

function OptionRow<T extends string>({
  title,
  options,
  value,
  onChange,
}: {
  title: string;
  options: { value: T; label: string; hint: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="space-y-2">
      <h4 className="font-pixel text-[9px] uppercase tracking-wider text-muted-foreground">
        {title}
      </h4>
      <div className="grid grid-cols-3 gap-2">
        {options.map((o) => (
          <button
            key={o.value}
            onClick={() => onChange(o.value)}
            className={cn(
              "rounded-md border px-2 py-2 text-left transition-colors",
              "focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50",
              value === o.value
                ? "border-primary bg-primary/10"
                : "border-border bg-card hover:bg-accent/20",
            )}
          >
            <div className="font-terminal text-lg leading-none">{o.label}</div>
            <div className="mt-1 font-terminal text-sm text-muted-foreground leading-tight">
              {o.hint}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

export function Controls({ pace, rations, onPace, onRations }: ControlsProps) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <OptionRow title="Pace" options={PACES} value={pace} onChange={onPace} />
      <OptionRow title="Rations" options={RATIONS} value={rations} onChange={onRations} />
    </div>
  );
}
